import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Header } from "../components/shared/Header";
import { WelcomeLanding } from "../components/shared/WelcomeLanding";
import { EventCard } from "../components/events/EventCard";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";
import { Button } from "../components/ui/button";
import { useEvents } from "../context/EventContext";

export function EventListPage() {
  const navigate = useNavigate();
  const { events, deleteEvent } = useEvents();
  const [eventToDelete, setEventToDelete] = useState<string | null>(null);

  const handleDeleteEvent = (eventId: string) => {
    setEventToDelete(eventId);
  };

  const confirmDeleteEvent = () => {
    if (eventToDelete) {
      deleteEvent(eventToDelete);
      setEventToDelete(null);
    }
  };

  // Sort events by most recent first
  const sortedEvents = [...events].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  return (
    <div className="bg-background min-h-screen">
      <Header title="Dividy" showLogo />

      <main className="p-4 space-y-4 max-w-lg mx-auto md:max-w-4xl">
        {sortedEvents.length === 0 ? (
          <WelcomeLanding onStart={() => navigate("/event/new")} />
        ) : (
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="space-y-3"
          >
            <AnimatePresence mode="popLayout">
              {sortedEvents.map((event) => (
                <motion.div
                  key={event.id}
                  variants={item}
                  layout
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                >
                  <EventCard
                    event={event}
                    onClick={() => navigate(`/event/${event.id}`)}
                    onDelete={() => handleDeleteEvent(event.id)}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </main>

      {sortedEvents.length > 0 && (
        <Button
          size="lg"
          className="fixed right-4 bottom-4 h-14 w-14 rounded-full shadow-lg mb-[env(safe-area-inset-bottom)] z-50"
          onClick={() => navigate("/event/new")}
        >
          <Plus className="h-6 w-6" />
        </Button>
      )}

      <ConfirmDialog
        open={eventToDelete !== null}
        onOpenChange={(open) => !open && setEventToDelete(null)}
        title="Eliminar evento"
        description="¿Estás seguro de que deseas eliminar este evento? Se perderán todos los participantes, grupos y gastos asociados."
        confirmLabel="Eliminar"
        variant="destructive"
        onConfirm={confirmDeleteEvent}
      />
    </div>
  );
}
